import React, { useEffect, useState, useMemo } from "react";
import api from "../../api";
import { toast } from "sonner";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Globe, MapPin, ServerCog, HelpCircle, AlertTriangle, RefreshCw, Ban, ShieldOff, Clock, Bot, ShieldCheck } from "lucide-react";
import { BlockIpControls } from "./BlockIpControls";

const pinIcon = (color) => L.divIcon({
  className: "",
  html: `<div style="width:18px;height:18px;border-radius:50% 50% 50% 0;background:${color};transform:rotate(-45deg);border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.45)"></div>`,
  iconSize: [18, 18],
  iconAnchor: [9, 18],
  popupAnchor: [0, -16],
});

const trustedPinIcon = () => L.divIcon({
  className: "",
  html: `<div style="width:24px;height:24px;border-radius:50% 50% 50% 0;background:#059669;transform:rotate(-45deg);border:2px solid #fff;box-shadow:0 1px 5px rgba(0,0,0,.5);display:flex;align-items:center;justify-content:center">
    <svg style="transform:rotate(45deg)" xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="m9 12 2 2 4-4"/></svg>
  </div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 24],
  popupAnchor: [0, -20],
});

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("it-IT", { timeZone: "Europe/Rome", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso || "—"; }
};

const locLabel = (p) => {
  if (p.status === "success") return [p.city, p.region, p.country].filter(Boolean).join(", ") || "Sconosciuta";
  if (p.status === "private") return "Rete interna/aziendale";
  if (p.status === "pending") return "In elaborazione…";
  return "Sconosciuta";
};

export const AccessMapTab = () => {
  const [points, setPoints] = useState([]);
  const [meta, setMeta] = useState({ total_ips: 0 });
  const [blocked, setBlocked] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ipFilter, setIpFilter] = useState("all");

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/access-logs/geo").then((r) => { setPoints(r.data.points || []); setMeta(r.data); }),
      api.get("/blocked-ips").then((r) => setBlocked(r.data || [])),
    ]).catch(() => toast.error("Errore nel caricamento della mappa")).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const blockedSet = useMemo(() => new Set(blocked.map((b) => b.ip)), [blocked]);

  const filteredPoints = useMemo(() => points.filter((p) => {
    if (ipFilter === "trusted") return !!p.whitelisted;
    if (ipFilter === "suspicious") return !p.whitelisted && (p.failed > 0 || blockedSet.has(p.ip));
    return true;
  }), [points, ipFilter, blockedSet]);

  const unblockIp = async (ip) => {
    try { await api.delete(`/blocked-ips/${ip}`); toast.success(`IP ${ip} segnalato come legittimo: accesso ripristinato`); load(); }
    catch { toast.error("Errore durante lo sblocco"); }
  };

  const markers = useMemo(() => filteredPoints.filter((p) => p.lat != null && p.lon != null), [filteredPoints]);

  const cityAgg = useMemo(() => {
    const m = {};
    points.forEach((p) => {
      const label = locLabel(p);
      const key = `${label}`;
      const g = m[key] || { label, country: p.status === "success" ? p.country : "", total: 0, ok: 0, failed: 0, ips: new Set(), last: null, kind: p.status };
      g.total += p.total; g.ok += p.ok; g.failed += p.failed; g.ips.add(p.ip);
      if (!g.last || (p.last_access && p.last_access > g.last)) g.last = p.last_access;
      m[key] = g;
    });
    return Object.values(m).map((g) => ({ ...g, ipCount: g.ips.size })).sort((a, b) => b.total - a.total);
  }, [points]);

  const totalOk = points.reduce((s, p) => s + p.ok, 0);
  const totalFailed = points.reduce((s, p) => s + p.failed, 0);

  return (
    <div data-testid="access-map-tab">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <p className="text-[13px] text-gray-600 max-w-[640px]">
          Provenienza geografica degli accessi al portale (ultimi 180 giorni). I pin rossi indicano località con accessi <b>falliti</b>: utili per individuare tentativi anomali.
        </p>
        <div className="flex items-center gap-3 flex-wrap">
          <select value={ipFilter} onChange={(e) => setIpFilter(e.target.value)} data-testid="map-ip-filter" className="border border-gray-300 rounded-sm px-3 py-2 text-[13px] outline-none focus:border-[#16345c] bg-white">
            <option value="all">Tutti gli IP</option>
            <option value="trusted">Solo IP fidati</option>
            <option value="suspicious">Solo IP sospetti</option>
          </select>
          <button onClick={load} disabled={loading} data-testid="access-map-refresh" className="flex items-center gap-2 border border-[#16345c] text-[#16345c] hover:bg-[#16345c] hover:text-white text-[13px] font-bold px-4 py-2 rounded-sm transition-colors disabled:opacity-60">
            <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Aggiorna
          </button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="bg-white border border-gray-200 rounded-lg p-3 flex items-center gap-3"><span className="w-10 h-10 rounded-lg bg-[#16345c]/10 text-[#16345c] flex items-center justify-center"><Globe size={20} /></span><div><div className="text-[20px] font-black text-[#16345c] leading-none">{meta.total_ips || 0}</div><div className="text-[11px] text-gray-500 mt-1">Indirizzi IP</div></div></div>
        <div className="bg-white border border-gray-200 rounded-lg p-3 flex items-center gap-3"><span className="w-10 h-10 rounded-lg bg-green-100 text-green-700 flex items-center justify-center"><MapPin size={20} /></span><div><div className="text-[20px] font-black text-[#16345c] leading-none">{totalOk}</div><div className="text-[11px] text-gray-500 mt-1">Accessi riusciti</div></div></div>
        <div className="bg-white border border-gray-200 rounded-lg p-3 flex items-center gap-3"><span className="w-10 h-10 rounded-lg bg-red-100 text-red-700 flex items-center justify-center"><AlertTriangle size={20} /></span><div><div className="text-[20px] font-black text-[#16345c] leading-none">{totalFailed}</div><div className="text-[11px] text-gray-500 mt-1">Accessi falliti</div></div></div>
      </div>

      <div className="flex items-center gap-4 flex-wrap mb-3 text-[12px] text-gray-600" data-testid="access-map-legend">
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-[#009640] border border-white shadow-sm"></span> Accessi riusciti</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-[#e30613] border border-white shadow-sm"></span> Accessi falliti</span>
        <span className="inline-flex items-center gap-1.5"><span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#059669] text-white"><ShieldCheck size={11} /></span> IP fidato (whitelist)</span>
      </div>

      <div className="rounded-lg overflow-hidden border border-gray-200 mb-6" style={{ height: 440 }} data-testid="access-map-container">
        <MapContainer center={[42.5, 12.5]} zoom={4} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false}>
          <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          {markers.map((p) => (
            <Marker key={p.ip} position={[p.lat, p.lon]} icon={p.whitelisted ? trustedPinIcon() : pinIcon(p.failed > 0 ? "#e30613" : "#009640")}>
              <Popup>
                <div style={{ fontSize: 12 }}>
                  <b>{locLabel(p)}</b><br />
                  IP: {p.ip}{p.whitelisted ? " (IP fidato)" : ""}<br />
                  Accessi: {p.total} (riusciti {p.ok}, falliti {p.failed})<br />
                  {p.users?.length ? <>Utenti: {p.users.join(", ")}<br /></> : null}
                  Ultimo: {fmtDate(p.last_access)}
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>

      {blocked.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6" data-testid="blocked-ips-panel">
          <h3 className="flex items-center gap-2 text-[14px] font-black text-red-700 uppercase tracking-tight mb-3"><Ban size={16} /> Indirizzi IP bloccati ({blocked.length})</h3>
          <div className="space-y-2">
            {blocked.map((b) => (
              <div key={b.ip} data-testid={`blocked-row-${b.ip}`} className="flex items-center justify-between gap-3 bg-white border border-red-100 rounded-md px-3 py-2 flex-wrap">
                <div>
                  <span className="font-mono text-[13px] font-bold text-[#16345c]">{b.ip}</span>
                  {b.auto && <span className="ml-1.5 inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-800 text-white align-middle"><Bot size={10} /> AUTO</span>}
                  <span className="ml-1.5 inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-700 align-middle">{b.block_type === "temporary" ? <><Clock size={10} /> fino al {fmtDate(b.expires_at)}</> : "permanente"}</span>
                  {b.reason ? <span className="text-[12px] text-gray-500"> · {b.reason}</span> : null}
                  <span className="text-[11px] text-gray-400"> · {b.blocked_by_name || "—"} il {fmtDate(b.created_at)}</span>
                </div>
                <button onClick={() => unblockIp(b.ip)} data-testid={`unblock-btn-${b.ip}`} className="flex items-center gap-1.5 text-[12px] font-bold text-[#16345c] border border-[#16345c] hover:bg-[#16345c] hover:text-white px-3 py-1.5 rounded-sm transition-colors"><ShieldOff size={13} /> Sblocca</button>
              </div>
            ))}
          </div>
        </div>
      )}

      <h3 className="text-[#16345c] text-[15px] font-black uppercase tracking-tight mb-3">Riepilogo per località</h3>
      <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto mb-8" data-testid="access-map-city-table">
        <table className="w-full min-w-[620px]">
          <thead>
            <tr className="bg-[#16345c] text-white text-[12px] uppercase tracking-wide">
              <th className="text-left px-4 py-3 font-bold">Località</th>
              <th className="text-left px-4 py-3 font-bold">IP</th>
              <th className="text-left px-4 py-3 font-bold">Accessi</th>
              <th className="text-left px-4 py-3 font-bold">Riusciti / Falliti</th>
              <th className="text-left px-4 py-3 font-bold">Ultimo accesso</th>
            </tr>
          </thead>
          <tbody>
            {cityAgg.map((g) => (
              <tr key={g.label} className="border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors">
                <td className="px-4 py-3">
                  <span className="inline-flex items-center gap-2 text-[14px] font-bold text-[#16345c]">
                    {g.kind === "private" ? <ServerCog size={14} className="text-gray-400" /> : g.kind === "success" ? <MapPin size={14} className="text-[#009640]" /> : <HelpCircle size={14} className="text-gray-400" />}
                    {g.label}
                  </span>
                </td>
                <td className="px-4 py-3 text-[13px] text-gray-600">{g.ipCount}</td>
                <td className="px-4 py-3 text-[13px] font-bold text-[#16345c]">{g.total}</td>
                <td className="px-4 py-3 text-[13px]">
                  <span className="text-green-700 font-semibold">{g.ok}</span> / <span className={g.failed > 0 ? "text-red-600 font-bold" : "text-gray-400"}>{g.failed}</span>
                </td>
                <td className="px-4 py-3 text-[13px] text-gray-600">{fmtDate(g.last)}</td>
              </tr>
            ))}
            {!loading && cityAgg.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-gray-400 text-[13px]">Nessun accesso registrato.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <h3 className="text-[#16345c] text-[15px] font-black uppercase tracking-tight mb-3">Dettaglio per IP</h3>
      <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto" data-testid="access-map-ip-table">
        <table className="w-full min-w-[680px]">
          <thead>
            <tr className="bg-[#16345c] text-white text-[12px] uppercase tracking-wide">
              <th className="text-left px-4 py-3 font-bold">Indirizzo IP</th>
              <th className="text-left px-4 py-3 font-bold">Località</th>
              <th className="text-left px-4 py-3 font-bold">Accessi</th>
              <th className="text-left px-4 py-3 font-bold">Falliti</th>
              <th className="text-left px-4 py-3 font-bold">Utenti</th>
              <th className="text-left px-4 py-3 font-bold">Azione</th>
            </tr>
          </thead>
          <tbody>
            {filteredPoints.map((p) => (
              <tr key={p.ip} data-testid={`access-ip-row-${p.ip}`} className={`border-b border-gray-100 last:border-0 transition-colors ${blockedSet.has(p.ip) ? "bg-red-50 hover:bg-red-100/70" : p.failed > 0 ? "bg-red-50/60 hover:bg-red-50" : "hover:bg-gray-50"}`}>
                <td className="px-4 py-3 text-[13px] font-mono text-gray-700">{p.ip}{blockedSet.has(p.ip) && <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-600 text-white align-middle">BLOCCATO</span>}{p.whitelisted && <span data-testid={`map-whitelisted-badge-${p.ip}`} className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 align-middle"><ShieldCheck size={11} /> fidato</span>}</td>
                <td className="px-4 py-3 text-[13px] text-gray-600">{locLabel(p)}</td>
                <td className="px-4 py-3 text-[13px] font-bold text-[#16345c]">{p.total}</td>
                <td className="px-4 py-3 text-[13px]">
                  {p.failed > 0
                    ? <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700"><AlertTriangle size={12} /> {p.failed}</span>
                    : <span className="text-gray-400">0</span>}
                </td>
                <td className="px-4 py-3 text-[12.5px] text-gray-500">{p.users?.join(", ") || "—"}</td>
                <td className="px-4 py-3">
                  <BlockIpControls ip={p.ip} blocked={blockedSet.has(p.ip)} onChanged={load} />
                </td>
              </tr>
            ))}
            {!loading && filteredPoints.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-400 text-[13px]">{points.length === 0 ? "Nessun accesso registrato." : "Nessun IP corrisponde al filtro selezionato."}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
